import React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteSearchQuery,
  getSearchQueries,
} from "api/clients/search-queries/search-queries.request";
import styled from "@emotion/styled";
import { IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import useSnackBar from "custom-hooks/useSnackBar.hook";
import { useLocale, useTranslations } from "next-intl";
import { useIntlPropertyTypesMap } from "shared/models/const/property-types";
import { useIntlImprovementsMap } from "shared/models/const/improvements.conts";
import LinkButton from "@components/link-button";
import { CPagesPath } from "shared/models/const/pages-path";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { parseRealestateFiltersToUrl } from "shared/models/functions/realestate-filters-helper";
import { ISearchQuery } from "@interfaces/search-query.interface";
import { useListJoiner } from "@helpers/list-joiner.helper";

export default function SearchQueriesList() {
  const { showMessage } = useSnackBar();
  const t = useTranslations("ProfileSearchQueries");
  const { refetch, data, isError, isLoading } = useQuery(
    ["search-query", "list"],
    getSearchQueries
  );
  const propertyTypeSlugsMap = useIntlPropertyTypesMap();
  const improvementsMap = useIntlImprovementsMap();
  const locale = useLocale();
  const listJoiner = useListJoiner(locale);
  const orJoiner = useListJoiner(locale, { type: "disjunction" });

  const { mutate } = useMutation(deleteSearchQuery, {
    onSuccess: () => {
      showMessage(t("deleteMessage"), {
        variant: "success",
        autoHideDuration: 2000,
      });
      refetch();
    },
    onError: () => showMessage("Error", { variant: "error" }),
  });

  const businessTypes = {
    sale: t("list_parameters.businesType.sale"),
    lease: t("list_parameters.businesType.lease"),
  };

  if (isLoading) {
    return <>{t("state.loading")}</>;
  }

  if (isError) {
    return <>{t("state.error")}</>;
  }

  if (data && data.data.length === 0) {
    return <>{t("state.no-data")}</>;
  }

  const createLink = (p: ISearchQuery["parameters"]) => {
    const { collection_properties, properties } = CPagesPath.es;
    const urlBase = p.collection ? collection_properties : properties;
    return urlBase + "/?" + parseRealestateFiltersToUrl(p as any);
  };

  return (
    <Container>
      {data &&
        data.data.map(({ id, parameters: p }, i) => (
          <SearchCardItem key={i}>
            <SearchCardItemHeader>
              <strong>{businessTypes[p.businessTypes]}</strong>
              <IconButton onClick={() => mutate(id)} sx={{ padding: 0.2 }}>
                <ClearIcon sx={{ maxWidth: 20, maxHeight: 20 }} />
              </IconButton>
            </SearchCardItemHeader>
            <SearchCardItemBody>
              {!!p.typeProperty?.length && (
                <p>
                  <strong>{t("descriptions.type")}: </strong>
                  {orJoiner.format(
                    p.typeProperty.map((k) => propertyTypeSlugsMap.get(k)?.name ?? "")
                  )}
                </p>
              )}
              {!!p.improvements?.length && (
                <p>
                  <strong>{t("descriptions.with")}: </strong>
                  {listJoiner.format(
                    p.improvements.map((k) => improvementsMap.get(+k) ?? "")
                  )}
                </p>
              )}
              {(!!p.locations?.length || !!p.sector?.length) && (
                <p className="noWrap">
                  <strong>{t("descriptions.in")}: </strong>
                  {p.locations?.map((v) => v.description).join(", ")}
                  {!!p.locations?.length && !!p.sector?.length ? " | " : null}
                  {!!p.sector?.length && (
                    <i>{p.sector?.map((v) => v.description).join(", ")}</i>
                  )}
                </p>
              )}
              {fieldValue(t("descriptions.bathrooms") + ": ", p.bathrooms)}
              {!!p.bedrooms?.length &&
                fieldValue(
                  t("descriptions.bedrooms") + ": ",
                  orJoiner.format(p.bedrooms).replace(">","+")
                )}
              {fieldValue(t("descriptions.stories") + ": ", p.floorLevels)}
              {fieldValue(t("descriptions.parkings") + ": ", p.parkings)}
              <p>
                <PriceRange parameters={p} />
              </p>
            </SearchCardItemBody>
            <SearchCardItemFooter>
              <LinkButton href={createLink(p)}>
                {t("apply-search")}{" "}
                <ArrowForwardIosIcon sx={{ fontSize: "13px" }} />
              </LinkButton>
            </SearchCardItemFooter>
            {p.collection && (
              <CollectionRibbon>
                <p>THE RE/MAX COLLECTION</p>
              </CollectionRibbon>
            )}
          </SearchCardItem>
        ))}
    </Container>
  );
}

function fieldValue(label: string, value: any) {
  if (value === null || value === undefined) return null;
  return (
    <p>
      <strong>{label} </strong>
      {value}
    </p>
  );
}

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function PriceRange({
  parameters: { amountMin, amountMax, currencyType },
}: {
  parameters: ISearchQuery["parameters"];
}) {
  const t = useTranslations("ProfileSearchQueries.descriptions.price-range");

  if (amountMin === amountMax && amountMax === null) return null;

  const c = currencyType.toUpperCase();

  if (amountMin === null || amountMax === null) {
    return (
      <span>
        {!!amountMax && (
          <>
            <strong>
              {t("max")}: {c}
            </strong>
            {moneyFormatter.format(amountMax)}
          </>
        )}
        {!!amountMin && (
          <>
            <strong>
              {t("min")}: {c}
            </strong>
            {moneyFormatter.format(amountMin)}
          </>
        )}
      </span>
    );
  }

  return (
    <span>
      <strong>
        {t("between")}: {c}
      </strong>
      {moneyFormatter.format(amountMin)} - <strong>{c}</strong>
      {moneyFormatter.format(amountMax)}
    </span>
  );
}

const Container = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 25px 16px;
  font-size: 0.9rem;
`;

const SearchCardItem = styled.div`
  background-color: white;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 20px 1fr 20px;
  grid-gap: 10px;
  border-radius: 4px;
  box-shadow: 0px 2px 3px #c6c6c6;
  padding: 15px;
  position: relative;

  &:hover {
    box-shadow: 0px 3px 5px 1px var(--color-brand-border);
  }
`;

const CollectionRibbon = styled.div`
  position: absolute;
  top: -24px;
  left: 10px;
  background-color: var(--color-brand-blue-collection-main);
  color: #fff;
  padding: 4px 10px 3px;
  border-radius: 10px 10px 0 0;
  letter-spacing: 1px;
  font-size: 0.9rem;
  font-weight: 400;
  text-transform: uppercase;
`;

const SearchCardItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SearchCardItemBody = styled.div`
  p + p {
    margin-top: 5px;
  }

  p:not(.noWrap) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SearchCardItemFooter = styled.div``;
